'use client';

import { createContext, useContext, useCallback, useMemo, ReactNode, useState, useEffect } from 'react';
import { DisclosureProps } from 'libs/contentful/types';
import { extractDisclosuresFromDocument, sortDisclosuresByPriority } from 'utils/disclosures';
import { DocumentWithEmbeddedEntries } from 'libs/contentful/types';

export interface DisclosureRegistration {
  moduleId: string;
  disclosures: DisclosureProps[];
  source: string; // e.g., 'cardCopy', 'accordionContent', etc.
}

interface DisclosureContextType {
  /** All registered disclosures from across the page */
  allDisclosures: DisclosureProps[];
  /** Register disclosures from a component */
  registerDisclosures: (registration: DisclosureRegistration) => void;
  /** Unregister disclosures when component unmounts */
  unregisterDisclosures: (moduleId: string) => void;
  /** Get the numbering map for embedded disclosures */
  getDisclosureNumberingMap: () => Map<string, number>;
  /** Get sorted disclosures for footer display */
  getSortedDisclosures: () => DisclosureProps[];
}

interface ConsumedDisclosuresContextType {
  /** Set of disclosure IDs that have been consumed by DisclosureAddOn modules */
  consumedDisclosureIds: Set<string>;
  /** Mark disclosures as consumed (shown in DisclosureAddOn) */
  consumeDisclosures: (moduleId: string, disclosureIds: string[]) => () => void;
  /** Check if a disclosure has been consumed */
  isDisclosureConsumed: (disclosureId: string) => boolean;
}

const DisclosureContext = createContext<DisclosureContextType | null>(null);
const ConsumedDisclosuresContext = createContext<ConsumedDisclosuresContextType | null>(null);

export interface DisclosureProviderProps {
  children: ReactNode;
  /** Initial disclosures extracted server-side */
  initialDisclosures?: DisclosureProps[];
}

/**
 * Context provider for page-level disclosure management.
 * Provides a centralized way to register and manage disclosures across all page components.
 * Supports both server-side extracted disclosures and client-side registered disclosures.
 *
 * @example
 * // Usage at page level with server-extracted disclosures
 * function PageTemplate({ modules }) {
 *   const serverDisclosures = extractPageDisclosures(modules);
 *
 *   return (
 *     <DisclosureProvider initialDisclosures={serverDisclosures}>
 *       <main>
 *         {modules.map(module => <Module key={module.id} {...module} />)}
 *       </main>
 *       <footer>
 *         <PageDisclosures />
 *       </footer>
 *     </DisclosureProvider>
 *   );
 * }
 *
 * @example
 * // Usage in components
 * function CardsIconModule({ cardItems }) {
 *   useDisclosureTracking({
 *     moduleId: 'cards-icon-123',
 *     content: cardItems.map(item => ({
 *       cardCopy: item.cardCopy,
 *       cardHeadlineRte: item.cardHeadlineRte,
 *       textEyebrowRte: item.textEyebrowRte
 *     }))
 *   });
 *
 *   return <div>...</div>;
 * }
 */
export function DisclosureProvider({ children, initialDisclosures = [] }: DisclosureProviderProps) {
  const [registrations, setRegistrations] = useState<Map<string, DisclosureRegistration>>(new Map());
  const [consumedRegistrations, setConsumedRegistrations] = useState<Map<string, string[]>>(new Map());

  const registerDisclosures = useCallback((registration: DisclosureRegistration) => {
    setRegistrations(prev => new Map(prev).set(registration.moduleId, registration));
  }, []);

  const unregisterDisclosures = useCallback((moduleId: string) => {
    setRegistrations(prev => {
      const newMap = new Map(prev);
      newMap.delete(moduleId);
      return newMap;
    });
  }, []);

  // Compute consumed disclosure IDs from all DisclosureAddOn registrations
  const consumedDisclosureIds = useMemo(() => {
    const ids = new Set<string>();
    consumedRegistrations.forEach(disclosureIds => {
      disclosureIds.forEach(id => ids.add(id));
    });
    return ids;
  }, [consumedRegistrations]);

  // Mark disclosures as consumed (shown in DisclosureAddOn)
  const consumeDisclosures = useCallback((moduleId: string, disclosureIds: string[]) => {
    setConsumedRegistrations(prev => new Map(prev).set(moduleId, disclosureIds));

    // Return cleanup function
    return () => {
      setConsumedRegistrations(prev => {
        const newMap = new Map(prev);
        newMap.delete(moduleId);
        return newMap;
      });
    };
  }, []);

  // Check if a disclosure has been consumed
  const isDisclosureConsumed = useCallback(
    (disclosureId: string) => {
      return consumedDisclosureIds.has(disclosureId);
    },
    [consumedDisclosureIds],
  );

  // Compute all disclosures from initial disclosures + client registrations
  const allDisclosures = useMemo(() => {
    const disclosures: DisclosureProps[] = [...initialDisclosures];

    // Add disclosures from client-side registrations
    registrations.forEach(registration => {
      disclosures.push(...registration.disclosures);
    });

    // Deduplicate by sys.id
    const uniqueDisclosures = disclosures.filter(
      (disclosure, index, array) => array.findIndex(d => d.sys?.id === disclosure.sys?.id) === index,
    );

    return uniqueDisclosures;
  }, [initialDisclosures, registrations]);

  // Create numbering map for embedded disclosures
  const getDisclosureNumberingMap = useCallback(() => {
    const map = new Map<string, number>();
    let autoNumberCounter = 0;

    const sortedDisclosures = sortDisclosuresByPriority(allDisclosures);

    sortedDisclosures.forEach(disclosure => {
      // Only assign numbers to disclosures that don't have symbols
      if (!disclosure.symbol && disclosure.sys?.id) {
        autoNumberCounter++;
        map.set(disclosure.sys.id, autoNumberCounter);
      }
    });

    return map;
  }, [allDisclosures]);

  // Get sorted disclosures for footer display
  const getSortedDisclosures = useCallback(() => {
    return sortDisclosuresByPriority(allDisclosures);
  }, [allDisclosures]);

  const disclosureValue = useMemo(
    () => ({
      allDisclosures,
      registerDisclosures,
      unregisterDisclosures,
      getDisclosureNumberingMap,
      getSortedDisclosures,
    }),
    [allDisclosures, registerDisclosures, unregisterDisclosures, getDisclosureNumberingMap, getSortedDisclosures],
  );

  const consumedValue = useMemo(
    () => ({
      consumedDisclosureIds,
      consumeDisclosures,
      isDisclosureConsumed,
    }),
    [consumedDisclosureIds, consumeDisclosures, isDisclosureConsumed],
  );

  return (
    <DisclosureContext.Provider value={disclosureValue}>
      <ConsumedDisclosuresContext.Provider value={consumedValue}>{children}</ConsumedDisclosuresContext.Provider>
    </DisclosureContext.Provider>
  );
}

/**
 * Hook to access disclosure context.
 * Must be used within a DisclosureProvider component.
 *
 * @returns {DisclosureContextType} Object containing disclosure state and management functions
 * @throws {Error} If used outside of a DisclosureProvider
 */
export function useDisclosureContext() {
  const context = useContext(DisclosureContext);

  if (!context) {
    throw new Error('useDisclosureContext must be used within a DisclosureProvider');
  }

  return context;
}

/**
 * Hook to safely access disclosure context without throwing an error.
 * Returns undefined if used outside of a DisclosureProvider.
 *
 * @returns {DisclosureContextType | undefined} Object containing disclosure state and management functions or undefined
 */
export function useDisclosureContextSafe() {
  return useContext(DisclosureContext);
}

/**
 * Hook for components to register their disclosures with the central provider.
 * Automatically extracts disclosures from rich text content and registers them.
 * This is primarily for client components that need to register disclosures dynamically.
 *
 * @param options Configuration for disclosure tracking
 * @param options.moduleId Unique identifier for the component/module
 * @param options.content Array of rich text content to scan for disclosures
 * @param options.source Optional source identifier (e.g., 'cardCopy', 'faqContent')
 */
export function useDisclosureTracking({
  moduleId,
  content,
  source = 'unknown',
}: {
  moduleId: string;
  content: (DocumentWithEmbeddedEntries | undefined)[];
  source?: string;
}) {
  const context = useDisclosureContextSafe();

  // Create a stable key for the content to detect actual changes
  const contentKey = useMemo(() => {
    const validContent = content.filter(Boolean);
    return validContent.map(doc => (doc?.json ? JSON.stringify(doc.json) : '')).join('|');
  }, [content]);

  useEffect(() => {
    // Early return inside useEffect if no provider is available
    if (!context) {
      return;
    }

    const { registerDisclosures, unregisterDisclosures } = context;

    // Extract disclosures from all provided content
    const disclosures: DisclosureProps[] = [];
    const validContent = content.filter(Boolean);

    validContent.forEach(document => {
      if (document) {
        const documentDisclosures = extractDisclosuresFromDocument(document);
        disclosures.push(...documentDisclosures);
      }
    });

    // Register with the provider
    if (disclosures.length > 0) {
      registerDisclosures({
        moduleId,
        disclosures,
        source,
      });
    }

    // Cleanup on unmount
    return () => {
      unregisterDisclosures(moduleId);
    };
  }, [moduleId, contentKey, source, context, content]);
}

/**
 * Hook to get all disclosures for footer display.
 * Returns disclosures sorted with symbols first, then auto-numbered disclosures.
 * Returns an empty array if used outside of a DisclosureProvider.
 *
 * @example
 * function PageDisclosures() {
 *   const disclosures = useDisclosures();
 *
 *   if (!disclosures.length) return null;
 *
 *   return (
 *     <div>
 *       {disclosures.map((disclosure, index) => (
 *         <Disclosure key={disclosure.sys?.id || index} {...disclosure} />
 *       ))}
 *     </div>
 *   );
 * }
 */
export function useDisclosures(): DisclosureProps[] {
  const context = useDisclosureContextSafe();
  if (!context) {
    return []; // Return empty array if no provider is available
  }
  return context.getSortedDisclosures();
}

/**
 * Hook to get the disclosure numbering map for embedded disclosures.
 * Used by rich text components to show the correct numbers for embedded disclosures.
 * Returns an empty Map if used outside of a DisclosureProvider.
 *
 * @example
 * function RichTextCopy({ copy }) {
 *   const disclosureNumberingMap = useDisclosureNumberingMap();
 *
 *   return documentToReactComponents(copy.json, {
 *     renderNode: {
 *       [INLINES.EMBEDDED_ENTRY]: (node, children) =>
 *         renderEmbeddedEntry(node, children, copy, disclosureNumberingMap),
 *     },
 *   });
 * }
 */
export function useDisclosureNumberingMap(): Map<string, number> {
  const context = useDisclosureContextSafe();
  if (!context) {
    return new Map(); // Return empty map if no provider is available
  }
  return context.getDisclosureNumberingMap();
}

/**
 * Hook to access consumed disclosures context for DisclosureAddOn modules.
 * Used to mark disclosures as "consumed" so they don't appear in DynamicDisclosures.
 *
 * @returns {ConsumedDisclosuresContextType} Object containing consumed disclosure management functions
 */
export function useConsumedDisclosures(): ConsumedDisclosuresContextType {
  const context = useContext(ConsumedDisclosuresContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    return {
      consumedDisclosureIds: new Set(),
      consumeDisclosures: () => () => {},
      isDisclosureConsumed: () => false,
    };
  }
  return context;
}
